import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import HomePage from './pages/HomePage';
import BookServicePage from './pages/BookServicePage';
import BrowseDriversPage from './pages/BrowseDriversPage';
import BookDriverPage from './pages/BookDriverPage';
import BookDriverRequestPage from './pages/BookDriverRequestPage';
import AdminPricingPage from './pages/AdminPricingPage';
import AdminDistanceRangesPage from './pages/AdminDistanceRangesPage';
import AdminUserManagementPage from './pages/AdminUserManagementPage';
import AdminComplaintsPage from './pages/AdminComplaintsPage';
import AdminNotificationSettingsPage from './pages/AdminNotificationSettingsPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import MyComplaintsPage from './pages/MyComplaintsPage';
import NewBookingPage from './pages/NewBookingPage';
import MyBookingsPage from './pages/MyBookingsPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import ProviderSchedulePage from './pages/ProviderSchedulePage';
import ProviderBookingsPage from './pages/ProviderBookingsPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminKycApprovalPage from './pages/AdminKycApprovalPage';
import AdminPoliceVerificationPage from './pages/AdminPoliceVerificationPage';
import AdminMarketingNotificationPage from './pages/AdminMarketingNotificationPage';
import AdminCityCoveragePage from './pages/AdminCityCoveragePage';
import AdminFeatureFlagsPage from './pages/AdminFeatureFlagsPage';
import KycPage from './pages/KycPage';
import PhoneVerificationPage from './pages/PhoneVerificationPage';
import ReviewPendingPage from './pages/ReviewPendingPage';
import DriverMapView from './pages/DriverMapView';
import CustomerMapView from './pages/CustomerMapView';
import DriverEarningsPage from './pages/DriverEarningsPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import FAQPage from './pages/FAQPage';
import { usePushNotifications } from './hooks/usePushNotifications';

// Protected Route Component with Provider Document Checking
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: string | string[] }> = ({ 
  children, 
  requiredRole 
}) => {
  const { isAuthenticated, user, loading, refreshUser } = useAuth();
  const location = useLocation();
  const lastUserIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    // Refresh user data when authenticated to get latest approval statuses
    // Only refresh when user ID changes (different user logged in) or when first authenticated
    const currentUserId = user?._id || user?.id || null;
    if (isAuthenticated && user && currentUserId && currentUserId !== lastUserIdRef.current) {
      lastUserIdRef.current = currentUserId;
      refreshUser();
    }
    // Reset ref when user logs out
    if (!isAuthenticated) {
      lastUserIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?._id || user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Provider document verification check
  // Skip this check if user is already on profile completion routes to avoid redirect loops
  const isProfileCompletionRoute = ['/complete-profile', '/kyc', '/review-pending'].includes(location.pathname);
  
  if (user?.role === 'provider' && !isProfileCompletionRoute) {
    const policeVerificationStatus = user.policeVerificationStatus || 'not_submitted';
    const kycStatus = user.kycStatus || 'not_submitted';
    const hasPoliceVerificationDocument = user.policeVerificationDocument && 
      user.policeVerificationDocument.trim() !== '';

    // NEW FLOW: KYC first, then police verification
    
    // Case 1: KYC not submitted - redirect to KYC first
    if (kycStatus === 'not_submitted') {
      return <Navigate to="/kyc" replace />;
    }

    // Case 2: KYC rejected - redirect to KYC for re-upload
    if (kycStatus === 'rejected') {
      return <Navigate to="/kyc" replace />;
    }

    // Case 3: KYC pending - wait for approval before police verification
    if (kycStatus === 'pending') {
      return <Navigate to="/review-pending" replace />;
    }

    // Case 4: KYC approved - now check police verification
    // Police verification not submitted - redirect to complete profile for police verification
    if (policeVerificationStatus === 'not_submitted' && !hasPoliceVerificationDocument) {
      return <Navigate to="/complete-profile" replace />;
    }

    // Case 5: Police verification rejected - redirect to complete profile for re-upload
    if (policeVerificationStatus === 'rejected') {
      return <Navigate to="/complete-profile" replace />;
    }

    // Case 6: Police verification pending - show review pending
    if (policeVerificationStatus === 'pending') {
      return <Navigate to="/review-pending" replace />;
    }

    // Case 7: Both documents approved - allow access
    // If both are approved, continue to render children
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user?.role || '')) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

// Layout Component
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};

// Auth Page Component
const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const { isAuthenticated } = useAuth();

  // If already authenticated, redirect to home (but let LoginForm handle redirects after login)
  React.useEffect(() => {
    if (isAuthenticated && !isLogin) {
      // Only redirect if on register page and authenticated
      // LoginForm will handle redirects after login
      window.location.href = '/';
    }
  }, [isAuthenticated, isLogin]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        {isLogin ? (
          <LoginForm onToggleMode={() => setIsLogin(false)} />
        ) : (
          <RegisterForm onToggleMode={() => setIsLogin(true)} />
        )}
      </main>
    </div>
  );
};

// Main App Component
const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  
  // Initialize push notifications when authenticated
  usePushNotifications(isAuthenticated);

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/login" element={<AuthPage />} />
        
        {/* Protected Routes */}
        <Route 
          path="/book-service/:id" 
          element={
            <ProtectedRoute requiredRole="customer">
              <Layout><BookServicePage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/browse-drivers" 
          element={
            <ProtectedRoute requiredRole="customer">
              <Layout><BrowseDriversPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/book-driver" 
          element={
            <ProtectedRoute>
              <Layout><BookDriverRequestPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/book-driver/:id" 
          element={
            <ProtectedRoute requiredRole="customer">
              <Layout><BookDriverPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/pricing" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminPricingPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/distance-ranges" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminDistanceRangesPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/city-coverage" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminCityCoveragePage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminUserManagementPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/book/:category" 
          element={
            <ProtectedRoute requiredRole="customer">
              <Layout><NewBookingPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/bookings" 
          element={
            <ProtectedRoute requiredRole={["customer", "admin"]}>
              <Layout><MyBookingsPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Layout><ProfilePage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/edit-profile" 
          element={
            <ProtectedRoute>
              <Layout><EditProfilePage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/complete-profile" 
          element={
            <ProtectedRoute>
              <CompleteProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/kyc" 
          element={
            <ProtectedRoute>
              <Layout><KycPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/phone-verification" 
          element={
            <PhoneVerificationPage />
          } 
        />
        <Route 
          path="/review-pending" 
          element={
            <ProtectedRoute>
              <ReviewPendingPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminDashboard /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/kyc-approval" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminKycApprovalPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/police-verification" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminPoliceVerificationPage /></Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/schedules"
          element={
            <ProtectedRoute requiredRole="provider">
              <Layout><ProviderSchedulePage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/provider-bookings"
          element={
            <ProtectedRoute requiredRole="provider">
              <Layout><ProviderBookingsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/driver-earnings"
          element={
            <ProtectedRoute requiredRole="provider">
              <Layout><DriverEarningsPage /></Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Map view routes */}
        <Route
          path="/driver-map/:bookingId"
          element={
            <ProtectedRoute requiredRole="provider">
              <Layout><DriverMapView /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/trip/:bookingId"
          element={
            <ProtectedRoute>
              <Layout><CustomerMapView /></Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Admin Complaint & Settings Routes */}
        <Route
          path="/admin/complaints"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminComplaintsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminAnalyticsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/notification-settings"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminNotificationSettingsPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/marketing-notification"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminMarketingNotificationPage /></Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feature-flags"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout><AdminFeatureFlagsPage /></Layout>
            </ProtectedRoute>
          }
        />
        
        {/* My Complaints Route (Customers and Providers) */}
        <Route
          path="/my-complaints"
          element={
            <ProtectedRoute>
              <Layout><MyComplaintsPage /></Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Public Legal Pages */}
        <Route path="/terms-and-conditions" element={<Layout><TermsAndConditionsPage /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
        <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Root App Component with Auth Provider and Error Boundary
const App: React.FC = () => {
  React.useEffect(() => {
    // Log version information on app start
    const { logVersion } = require('./utils/version');
    logVersion().catch(() => {
      // Ignore errors, version logging is non-critical
    });
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
